// Unified stats page. Reads the caller's cross-game stats from the shared stats
// read API and renders lifetime totals (including favorite game), per-game
// win/loss with playtime, per-partner couples stats (selected from a dropdown
// of the top 10 most-played partners), and recent match history.

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  StatsCouple,
  StatsMatch,
  StatsPlayerGame,
  StatsProfileResponse,
  StatsUser,
  UserLookupResponse,
} from "@couplesgames/core-types";
import { useAuth } from "../store/auth.js";
import { config } from "../lib/config.js";
import { gameNameForSlug } from "../lib/games.js";

function formatDuration(ms: number): string {
  if (ms <= 0) return "—";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDate(epochMs: number): string {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function matchResult(match: StatsMatch, userId: string | null): { label: string; tone: string } {
  if (match.winnerUserId === null) return { label: "D", tone: "text-amber-400" };
  if (match.winnerUserId === userId) return { label: "W", tone: "text-emerald-400" };
  return { label: "L", tone: "text-rose-400" };
}

function userLabel(sub: string, names: Record<string, StatsUser>): string {
  const u = names[sub];
  if (u?.name) return u.name;
  if (u?.email) return u.email;
  return "Player";
}

function userPicture(sub: string, names: Record<string, StatsUser>): string | undefined {
  return names[sub]?.picture;
}

function partnerId(couple: StatsCouple, userId: string | null): string {
  return couple.playerIds.find((id) => id !== userId) ?? couple.playerIds[1] ?? "";
}

function coupleTotal(couple: StatsCouple): number {
  return couple.perGame.reduce((sum, g) => sum + g.games, 0);
}

function Avatar({ src, name, size = "md" }: { src?: string; name: string; size?: "sm" | "md" }): JSX.Element {
  const cls = size === "sm" ? "h-5 w-5 text-[10px]" : "h-8 w-8 text-sm";
  if (src) {
    return <img src={src} alt={name} className={`${cls} rounded-full border border-slate-700 object-cover`} />;
  }
  return (
    <div className={`${cls} rounded-full border border-slate-700 bg-slate-800 flex items-center justify-center text-slate-300`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

async function fetchUserNames(
  subs: string[],
  getAccessToken: () => Promise<string | null>,
): Promise<Record<string, StatsUser>> {
  const unique = [...new Set(subs.filter(Boolean))];
  if (unique.length === 0) return {};
  try {
    const token = await getAccessToken();
    const res = await fetch(`${config.statsApi}/api/stats/users`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ subs: unique }),
    });
    if (!res.ok) return {};
    const body = (await res.json()) as UserLookupResponse;
    return body.users ?? {};
  } catch {
    return {};
  }
}

export function StatsPage(): JSX.Element {
  const { ready, session, userId, login, getAccessToken } = useAuth();
  const [data, setData] = useState<StatsProfileResponse | null>(null);
  const [names, setNames] = useState<Record<string, StatsUser>>({});
  const [selectedPairKey, setSelectedPairKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessToken();
        if (cancelled) return;
        const res = await fetch(`${config.statsApi}/api/stats/profile`, {
          headers: {
            accept: "application/json",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) {
          setError(`stats request failed (${res.status})`);
          return;
        }
        const body = (await res.json()) as StatsProfileResponse;
        if (!cancelled) setData(body);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, getAccessToken]);

  // Resolve opponent display names once the profile loads.
  useEffect(() => {
    if (!data) return;
    const subs = new Set<string>();
    for (const m of data.matches) for (const id of m.playerIds) subs.add(id);
    for (const c of data.couples) for (const id of c.playerIds) subs.add(id);
    let cancelled = false;
    void fetchUserNames([...subs], getAccessToken).then((n) => {
      if (!cancelled) setNames(n);
    });
    return () => {
      cancelled = true;
    };
  }, [data, getAccessToken]);

  // Default the partner selector to the most-played couple.
  useEffect(() => {
    if (data && data.couples.length > 0 && selectedPairKey === null) {
      setSelectedPairKey(data.couples[0]?.pairKey ?? null);
    }
  }, [data, selectedPairKey]);

  const favorite = useMemo(() => {
    if (!data) return null;
    return data.perGame.reduce<StatsPlayerGame | null>(
      (best, g) => (!best || g.games > best.games ? g : best),
      null,
    );
  }, [data]);

  const totals = useMemo(() => {
    if (!data) return { wins: 0, losses: 0, draws: 0 };
    return data.perGame.reduce(
      (acc, g) => ({
        wins: acc.wins + g.wins,
        losses: acc.losses + g.losses,
        draws: acc.draws + g.draws,
      }),
      { wins: 0, losses: 0, draws: 0 },
    );
  }, [data]);

  const selectedCouple = useMemo(() => {
    if (!data || data.couples.length === 0) return null;
    return data.couples.find((c) => c.pairKey === selectedPairKey) ?? data.couples[0] ?? null;
  }, [data, selectedPairKey]);

  if (!ready) {
    return (
      <main className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center text-slate-400">
        loading…
      </main>
    );
  }

  if (!session) {
    return (
      <main className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center px-6">
        <div className="w-full max-w-sm text-center space-y-4">
          <h1 className="text-2xl font-semibold">Sign in to view your stats</h1>
          <button
            onClick={() => void login("/stats")}
            className="w-full rounded-xl bg-white text-slate-900 font-semibold py-3 hover:bg-slate-200 transition"
          >
            Sign in with Google
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="px-6 py-12">
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <header>
          <h1 className="text-3xl font-bold tracking-tight">Stats</h1>
          <p className="text-slate-400">Your games across every title.</p>
        </header>

        {loading && <p className="text-slate-400">loading…</p>}
        {error && <p className="text-rose-400">{error}</p>}

        {!loading && !error && data && (
          <>
            <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Overview</h2>
              <div className="mt-3 flex gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold">{data.profile?.games ?? 0}</div>
                  <div className="text-xs text-slate-400">games</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{formatDuration(data.profile?.playtimeMs ?? 0)}</div>
                  <div className="text-xs text-slate-400">playtime</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">
                    {favorite ? gameNameForSlug(favorite.gameSlug) : "—"}
                  </div>
                  <div className="text-xs text-slate-400">favorite game</div>
                </div>
              </div>
              <div className="mt-4 flex gap-8 border-t border-slate-800 pt-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-emerald-400">{totals.wins}</div>
                  <div className="text-xs text-slate-400">wins</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-rose-400">{totals.losses}</div>
                  <div className="text-xs text-slate-400">losses</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400">{totals.draws}</div>
                  <div className="text-xs text-slate-400">draws</div>
                </div>
              </div>
            </section>

            {data.perGame.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">By game</h2>
                {data.perGame.map((g) => (
                  <GameRow key={g.gameSlug} game={g} />
                ))}
              </section>
            )}

            {data.couples.length > 0 && selectedCouple && (
              <section className="space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">As a couple</h2>
                <PartnerDropdown
                  couples={data.couples}
                  userId={userId}
                  names={names}
                  selectedPairKey={selectedCouple.pairKey}
                  onSelect={setSelectedPairKey}
                />
                <CoupleCard couple={selectedCouple} userId={userId} names={names} />
              </section>
            )}

            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Recent games</h2>
              {data.matches.length === 0 ? (
                <p className="text-sm text-slate-500">No games yet. Go play something!</p>
              ) : (
                <ul className="space-y-2">
                  {data.matches.map((m) => (
                    <MatchRow
                      key={`${m.endedAt}-${m.roomCode}`}
                      match={m}
                      userId={userId}
                      names={names}
                    />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}

function GameRow({ game }: { game: StatsPlayerGame }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="font-medium">{gameNameForSlug(game.gameSlug)}</span>
        <span className="text-sm text-slate-400">{game.games} game{game.games === 1 ? "" : "s"}</span>
      </div>
      <div className="mt-1 flex gap-4 text-sm">
        <span className="text-emerald-400">{game.wins}W</span>
        <span className="text-rose-400">{game.losses}L</span>
        <span className="text-amber-400">{game.draws}D</span>
        <span className="text-slate-500">{formatDuration(game.playtimeMs)}</span>
      </div>
    </div>
  );
}

function PartnerDropdown({
  couples,
  userId,
  names,
  selectedPairKey,
  onSelect,
}: {
  couples: StatsCouple[];
  userId: string | null;
  names: Record<string, StatsUser>;
  selectedPairKey: string;
  onSelect: (pairKey: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = couples.find((c) => c.pairKey === selectedPairKey) ?? couples[0];
  if (!selected) return <></>;
  const selPartner = partnerId(selected, userId);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3 transition hover:border-slate-600"
      >
        <Avatar src={userPicture(selPartner, names)} name={userLabel(selPartner, names)} />
        <span className="flex-1 text-left font-medium">{userLabel(selPartner, names)}</span>
        <span className="text-xs text-slate-500">{coupleTotal(selected)} games</span>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="text-slate-500">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-xl"
        >
          {couples.map((c) => {
            const pid = partnerId(c, userId);
            const active = c.pairKey === selectedPairKey;
            return (
              <li key={c.pairKey}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onSelect(c.pairKey);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-slate-800 ${
                    active ? "bg-slate-800/60" : ""
                  }`}
                >
                  <Avatar src={userPicture(pid, names)} name={userLabel(pid, names)} size="sm" />
                  <span className="flex-1 truncate">{userLabel(pid, names)}</span>
                  <span className="text-xs text-slate-500">{coupleTotal(c)} games</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CoupleCard({
  couple,
  userId,
  names,
}: {
  couple: StatsCouple;
  userId: string | null;
  names: Record<string, StatsUser>;
}): JSX.Element {
  const total = coupleTotal(couple);
  const recent = couple.recentMatches.slice(0, 10);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Head to head</h3>
        <span className="text-sm text-slate-400">{total} game{total === 1 ? "" : "s"} together</span>
      </div>
      {couple.perGame.length > 0 && (
        <div className="mt-3 space-y-2">
          {couple.perGame.map((g) => (
            <div key={g.gameSlug} className="flex items-center justify-between text-sm">
              <span>{gameNameForSlug(g.gameSlug)}</span>
              <span className="text-slate-400">
                <span className="text-emerald-400">{g.wins}W</span>{" "}
                <span className="text-rose-400">{g.losses}L</span>{" "}
                <span className="text-amber-400">{g.draws}D</span>
              </span>
            </div>
          ))}
        </div>
      )}
      {recent.length > 0 && (
        <div className="mt-4 border-t border-slate-800 pt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Last {recent.length} games
          </h4>
          <ul className="mt-2 space-y-2">
            {recent.map((m) => (
              <MatchRow
                key={`${m.endedAt}-${m.roomCode}`}
                match={m}
                userId={userId}
                names={names}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MatchRow({
  match,
  userId,
  names,
}: {
  match: StatsMatch;
  userId: string | null;
  names: Record<string, StatsUser>;
}): JSX.Element {
  const result = matchResult(match, userId);
  const opponent = match.playerIds.find((id) => id !== userId) ?? match.playerIds[0] ?? "";
  return (
    <li className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3 text-sm">
      <div className="flex items-center gap-3">
        <span className={`font-semibold ${result.tone}`}>{result.label}</span>
        <span className="text-slate-300">{gameNameForSlug(match.gameSlug)}</span>
        {opponent && (
          <span className="flex items-center gap-1.5 text-slate-400">
            <span className="text-slate-500">vs</span>
            <Avatar src={userPicture(opponent, names)} name={userLabel(opponent, names)} size="sm" />
            <span>{userLabel(opponent, names)}</span>
          </span>
        )}
      </div>
      <div className="text-right text-xs text-slate-500">
        <div>{formatDate(match.endedAt)}</div>
        <div>{formatDuration(match.durationMs)} · {match.turns} turns</div>
      </div>
    </li>
  );
}
